import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

import { Globals } from 'app/_models/globals';
import { NotificationService } from 'app/_services/notification.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { SupportSellerVacationService } from '../../seller-vacation.service';

import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { HoldingSupplier } from '../../_models/holdingSupplier';
import { HoldingUser } from '../../_models/holdingUser';
import { SellerOnVacationCreation } from '../../_models/sellerOnVacationCreation';
import { AutoCompleteOption } from 'app/_componentes/input-auto-complete/_models/autoCompleteOption';

import { createDate } from 'app/_utils/general-utils';
import { UserService } from 'app/_services/user.service';
import { RolesIdsEnum } from 'app/_models/roles';

@Component({
    selector: 'app-seller-vacation-manage', templateUrl: './seller-vacation-manage.component.html', styleUrls: ['./seller-vacation-manage.component.scss'],
    standalone: false
})
export class SupportSellerVacationManageComponent {
    @Output() goBackToList = new EventEmitter<boolean>();

    holdings: AutoCompleteOption[] = [];
    holdingsSellers: AutoCompleteOption[] = [];
    holdingsSellersSubstitute: AutoCompleteOption[] = [];

    minEndVacationDate: NgbDate;
    validationMessages: any;

    fb = inject(FormBuilder);

    holdingNameControl: FormControl<string>;
    sellerNameControl: FormControl<string>;
    sellerSubstituteNameControl: FormControl<string>;
    endDateControl: FormControl<string>;

    holdingManageForm = this.fb.group({
        holdingName: ["", [Validators.required]],
        sellerName: ["", [Validators.required]],
        sellerSubstituteName: ["", [Validators.required]],
        endDate: ["", [Validators.required]]
    });

    constructor(private sellerVacationService: SupportSellerVacationService, private userService: UserService, private notificationService: NotificationService, private ngxLoader: NgxUiLoaderService, private globals: Globals) {
        this.validationMessages = {
            holdingName: {
                required: this.globals.translate('support.seller-vacation.form.validation-message.holding-id.required')
            },
            sellerName: {
                required: this.globals.translate('support.seller-vacation.form.validation-message.seller-id.required')
            },
            sellerSubstituteName: {
                required: this.globals.translate('support.seller-vacation.form.validation-message.seller-substitute-id.required')
            },
            endDate: {
                required: this.globals.translate('support.seller-vacation.form.validation-message.end-date.required')
            }
        };
    }

    ngOnInit() {
        this.handleSuppliersHoldingsLoading();

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        this.minEndVacationDate = { year: tomorrow.getFullYear(), month: tomorrow.getMonth() + 1, day: tomorrow.getDate() } as NgbDate;

        this.holdingNameControl = this.holdingManageForm.get('holdingName') as FormControl;
        this.sellerNameControl = this.holdingManageForm.get('sellerName') as FormControl;
        this.sellerSubstituteNameControl = this.holdingManageForm.get('sellerSubstituteName') as FormControl;
        this.endDateControl = this.holdingManageForm.get('endDate') as FormControl;

        this.sellerNameControl.disable();
        this.sellerSubstituteNameControl.disable();
        this.endDateControl.disable();

        this.holdingNameControl.valueChanges.subscribe((value) => {
            if (!value) {
                this.disableControl(this.sellerNameControl);
                this.disableControl(this.sellerSubstituteNameControl);
                this.disableControl(this.endDateControl);
                return;
            }

            this.handleSuppliersUsersLoading();
        });

        this.sellerNameControl.valueChanges.subscribe((value) => {
            if (!value) {
                this.disableControl(this.sellerSubstituteNameControl);
                this.disableControl(this.endDateControl);
                return;
            }

            this.holdingsSellersSubstitute = this.holdingsSellers.filter((seller: AutoCompleteOption) => {
                return seller.name !== value;
            });

            this.enableControl(this.sellerSubstituteNameControl);
            this.disableControl(this.endDateControl);
        });

        this.sellerSubstituteNameControl.valueChanges.subscribe((value) => {
            if (!value){
                this.disableControl(this.endDateControl);
                return;
            }

            this.endDateControl.enable();
        });
    }

    close() {
        this.goBackToList.emit(false);
    }

    handleSuppliersHoldingsLoading() {
        this.ngxLoader.startLoader('loader-principal');

        this.sellerVacationService.getSupplierHoldings().subscribe({
            next: (response: ApiResponseModel<HoldingSupplier[]>) => {
                this.holdings = response.result.map((holding: HoldingSupplier) => {
                    return { value: holding.holdingId, name: holding.holdingName };
                });

                if (this.userService.userRole() === RolesIdsEnum.GestorDePecas) {
                    this.holdings = this.holdings.filter((holding: AutoCompleteOption) => {
                        return holding.value === this.userService.holdingId();
                    });

                    this.holdingNameControl.setValue(this.holdings[0]?.name || '');
                    this.holdingNameControl.disable();
                }

                this.ngxLoader.stopLoader('loader-principal');
            },
            error: () => {
                this.notificationService.showErrorToastr('support.seller-vacation.messages.error.get-supplier-holdings', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    handleSuppliersUsersLoading() {
        this.ngxLoader.startLoader('loader-principal');

        var holdingName = this.holdingNameControl.value;

        const holding = this.holdings.filter((holding: AutoCompleteOption) => {
            return holding.name === holdingName;
        })[0];

        this.sellerVacationService.getSupplierHoldingsUsers(holding.value).subscribe({
            next: (response: ApiResponseModel<HoldingUser[]>) => {
                this.holdingsSellers = response.result.map((user: HoldingUser) => {
                    return { value: user.userId, name: user.name };
                });

                this.enableControl(this.sellerNameControl);
                this.disableControl(this.sellerSubstituteNameControl);
                this.disableControl(this.endDateControl);

                this.ngxLoader.stopLoader('loader-principal');
            },
            error: () => {
                this.notificationService.showErrorToastr('support.seller-vacation.messages.error.get-supplier-users', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    save() {
        if (!this.isFormValid())
            return;

        this.ngxLoader.startLoader('loader-principal');

        this.sellerVacationService.startSellerVacation(this.getSellerOnVacationData()).subscribe({
            next: () => {
                this.notificationService.showSuccessToastr(this.globals.translate('support.seller-vacation.messages.success.create-vacation'));
                this.ngxLoader.stopLoader('loader-principal');
                this.goBackToList.emit(true);
            },
            error: () => {
                this.notificationService.showErrorToastr(this.globals.translate('support.seller-vacation.messages.error.create-vacation'));
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    public disableControl(control: FormControl) {
        control.setValue('');
        control.disable();
    }

    private enableControl(control: FormControl) {
        control.setValue('');
        control.markAsUntouched();
        control.markAsPristine();
        control.updateValueAndValidity();
        control.enable();
    }

    private getSellerOnVacationData(): SellerOnVacationCreation {
        const formValue = this.holdingManageForm.getRawValue();

        const holding = this.holdings.filter((holding: AutoCompleteOption) => {
            return holding.name === formValue.holdingName;
        })[0];

        const seller = this.holdingsSellers.filter((seller: AutoCompleteOption) => {
            return seller.name === formValue.sellerName;
        })[0];

        const substituteSeller = this.holdingsSellers.filter((seller: AutoCompleteOption) => {
            return seller.name === formValue.sellerSubstituteName;
        })[0];

        return {
            holdingId: holding.value,
            sellerId: seller.value,
            substituteSellerId: substituteSeller.value,
            endDate: createDate(formValue.endDate)
        };
    }

    private isFormValid() {
        this.notificationService.clearToastr();

        Object.keys(this.holdingManageForm.controls).forEach(field => {
            const control = this.holdingManageForm.get(field);
            control?.markAsTouched({ onlySelf: true });
        });

        if (this.holdingManageForm.valid)
            return true;

        Object.keys(this.validationMessages).forEach(field => {
            const control = this.holdingManageForm.get(field);
            const errorMessages = this.validationMessages[field];

            if (control?.invalid) {
                Object.keys(errorMessages).forEach(errorKey => {
                    if (control.errors?.[errorKey]) {
                        this.notificationService.showErrorToastr(errorMessages[errorKey]);
                    }
                });
            }
        });

        return false;
    }
}
